/* eslint-disable prettier/prettier */
/* eslint-disable max-lines */
/* eslint-disable no-empty */
import { entries } from 'lodash'
import { isValid, parse } from 'date-fns'
import { useContext } from 'react'
import IFormValues, { CompareValues, FieldInit, IFormValue, Validator } from './IFormValues'
import {
  COMMON_CYRILLIC_ALPHABET,
  EMAIL_MAX_LENGTH,
  MAIL_REGEX,
  MINIMAL_AGE,
  MINIMAL_YEAR,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  ORGANIZATION_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEX,
  PHONE_CALL_CODE_MIN_LENGTH,
  PHONE_SMS_CODE_MIN_LENGTH,
  POSITION_MIN_LENGTH,
  UNIVERSITY_MIN_LENGTH,
  UZ_LATIN_ALPHABET,
} from './constants'
import { calculateAge, getPreparedMask } from '../components/DatePickerField/utils'
import { DATE_MASK, MASK_CHAR } from '../components/DatePickerField/constants'
import { DeployTypes } from '../emun/DeployTypes'
import { checkPhoneValid, formatPhoneValue, getValidationOptions } from '../components/PhoneInput2/utils'
import { FormFieldTypes } from '../emun/RegistrationFormFieldTypes'
import { IOrganizationsItem } from '../interfaces/IOrganizationsItem'
import {
  BIRTH_DATE_INCORRECT_TEXT,
  BIRTH_DATE_REQUIRED_TEXT,
  SPECIALTY_REQUIRED_TEXT,
  INSTITUTE_REQUIRED_TEXT,
  EMAIL_ALREADY_EXISTS_TEXT,
  EMAIL_FORMAT_TEXT,
  EMAIL_MAX_LENGTH_TEXT,
  EMAIL_REQUIRED_TEXT,
  EMAIL_TEMP_TEXT,
  FIRST_NAME_ALLOWED_SYMBOLS_TEXT,
  FIRST_NAME_ALLOWED_UZ_SYMBOLS_TEXT,
  FIRST_NAME_MAX_LENGTH_TEXT,
  FIRST_NAME_MIN_LENGTH_TEXT,
  FIRST_NAME_REQUIRED_TEXT,
  LAST_NAME_ALLOWED_SYMBOLS_TEXT,
  LAST_NAME_ALLOWED_UZ_SYMBOLS_TEXT,
  LAST_NAME_MAX_LENGTH_TEXT,
  LAST_NAME_MIN_LENGTH_TEXT,
  LAST_NAME_REQUIRED_TEXT,
  MIDDLE_NAME_ALLOWED_SYMBOLS_TEXT,
  MIDDLE_NAME_ALLOWED_UZ_SYMBOLS_TEXT,
  MIDDLE_NAME_MAX_LENGTH_TEXT,
  MIDDLE_NAME_MIN_LENGTH_TEXT,
  MIDDLE_NAME_REQUIRED_TEXT,
  ORGANIZATION_MIN_LENGTH_TEXT,
  ORGANIZATION_REQUIRED_TEXT,
  PASSWORD_ALLOWED_SYMBOLS_TEXT,
  PASSWORD_MAX_LENGTH_TEXT,
  PASSWORD_MIN_LENGTH_TEXT,
  PASSWORD_REQUIRED_SYMBOLS_TEXT,
  PASSWORD_REQUIRED_TEXT,
  PHONE_CALL_CODE_INCORRECT_TEXT,
  PHONE_CALL_NUMBER_REQUIRED_TEXT,
  PHONE_INVALID_TEXT,
  PHONE_INVALID_TEXT_UZ,
  PHONE_REQUIRED_TEXT,
  PHONE_REQUIRED_TEXT_UZ,
  PHONE_SMS_CODE_REQUIRED_TEXT,
  POSITION_MIN_TEXT,
  POSITION_REQUIRED_TEXT,
  POST_STUDENT_REQUIRED_TEXT,
  POST_STUDENT_OR_TEACHER_REQUIRED_TEXT,
  STAFF_REQUIRED_TEXT,
  TAXATION_REQUIRED_TEXT,
  UNIVERSITY_MIN_LENGTH_TEXT,
  UNIVERSITY_REQUIRED_TEXT,
  REGION_REQUIRED_TEXT,
} from './errorsValidation'
import { LanguageContext } from '../components/LanguageContext/LanguageContext'
import { LocaleContext } from '../components/LocaleContext/LocaleContext'
import { Languages } from '../interfaces/common'
import { useEnv } from '../utils/useEnv'
import { useCheckEmailRegistration } from '../api/mutations/useCheckEmailRegistration'
import { IDictionaryItem } from '../interfaces/IDictionaryItem'
import {
  FROM_101_TO_300,
  FROM_10_TO_30,
  FROM_301_TO_1000,
  FROM_31_TO_50,
  FROM_51_TO_100,
  LESS_THAN_10,
  MORE_1000,
  NONE,
  NOT_WORK,
  PHONE_ALREADY_EXISTS_TEXT,
} from '../components/formComponents/constants'
import { ISpecialtyItem } from '../interfaces/ISpecialtyItem'
import { IInstituteItem } from '../interfaces/IInstituteItem'
import { IRegionItem } from '../interfaces/IRegionItem'

type Validators = {
  [key in FormFieldTypes]: Validator
}

type Compares = {
  [key in FormFieldTypes]: CompareValues
}

export const getLanguageRegExp = (language: Languages, locale?: Languages): RegExp => {
  const shouldUseLatinAlphabet = language === Languages.UZ || locale === Languages.UZ
  const alphabet = shouldUseLatinAlphabet ? `${COMMON_CYRILLIC_ALPHABET}${UZ_LATIN_ALPHABET}` : COMMON_CYRILLIC_ALPHABET

  return new RegExp(`^[${alphabet}]+$`)
}

export const max = (value: string, maxLenght: number): boolean => value.length > maxLenght

export const min = (value: string, maxLenght: number): boolean => value.length < maxLenght

export const regex = (value: string, regExp: RegExp): boolean => !regExp.test(value)

export const stringCompare = (prevValue: IFormValue, currentValue: IFormValue) =>
  prevValue !== currentValue || !currentValue

export const dictionaryItemCompare = (
  prevValue: IFormValue | IDictionaryItem,
  currentValue: IFormValue | IDictionaryItem,
) => {
  const prev = prevValue as IDictionaryItem
  const current = currentValue as IDictionaryItem

  if (prev.value && current.name) {
    return !(prev.name === current.name && prev.value === current.value) || !current.name
  }

  return prev.name !== current.name || !current.name
}

export const university = async (value: IFormValue): Promise<string> => {
  const organizationValue = value as IOrganizationsItem
  const trimValue = organizationValue.name.trim()

  if (!trimValue) return UNIVERSITY_REQUIRED_TEXT
  if (min(trimValue, UNIVERSITY_MIN_LENGTH)) return UNIVERSITY_MIN_LENGTH_TEXT

  return ''
}

export const password = async (value: IFormValue): Promise<string> => {
  const stringValue = value as string

  return passwordValidator(stringValue)
}

export const postStudent = async (value: IFormValue): Promise<string> => {
  const organizationValue = value as IOrganizationsItem
  const trimValue = organizationValue.name.trim()

  if (!trimValue) return POST_STUDENT_REQUIRED_TEXT

  return ''
}

export const postStudentOrTeacher = async (value: IFormValue): Promise<string> => {
  const organizationValue = value as IOrganizationsItem
  const trimValue = organizationValue.name.trim()

  if (!trimValue) return POST_STUDENT_OR_TEACHER_REQUIRED_TEXT

  return ''
}

export const regionId = async (value: IFormValue): Promise<string> => {
  const val = value as IRegionItem
  const trimValue = val.id?.trim()

  if (!trimValue) return REGION_REQUIRED_TEXT

  return ''
}

export const instituteId = async (value: IFormValue): Promise<string> => {
  const val = value as IInstituteItem
  const trimValue = val.id?.trim()

  if (!trimValue) return INSTITUTE_REQUIRED_TEXT

  return ''
}

export const specialtyId = async (value: IFormValue): Promise<string> => {
  const val = value as ISpecialtyItem
  const trimValue = val.id?.trim()

  if (!trimValue) return SPECIALTY_REQUIRED_TEXT

  return ''
}

export const degreeId = async (): Promise<string> => {
  return ''
}

export const getGAError = (error: string) => {
  switch (error) {
    case LESS_THAN_10:
      return 'Less10'
    case FROM_10_TO_30:
      return '10-30'
    case FROM_31_TO_50:
      return '31-50'
    case FROM_51_TO_100:
      return '51-100'
    case FROM_101_TO_300:
      return '101-300'
    case FROM_301_TO_1000:
      return '301-1000'
    case MORE_1000:
      return 'More1000'
    case NOT_WORK:
      return 'NotWork'
    case NONE:
      return 'None'
    case PHONE_ALREADY_EXISTS_TEXT:
      return 'PhoneBusy'
    default:
      return ''
  }
}

export const codeValidator = (value = '', isCallCode?: boolean, phoneFieldType?: boolean): string => {
  const trimValue = value.replace(/\s/g, '')

  if (isCallCode && !trimValue) {
    return PHONE_CALL_NUMBER_REQUIRED_TEXT
  }

  if (!isCallCode && !trimValue && !phoneFieldType) {
    return PHONE_SMS_CODE_REQUIRED_TEXT
  }

  if (!isCallCode && !trimValue && phoneFieldType) {
    return PHONE_CALL_NUMBER_REQUIRED_TEXT
  }

  if (isCallCode && min(trimValue, PHONE_CALL_CODE_MIN_LENGTH)) {
    return PHONE_CALL_CODE_INCORRECT_TEXT
  }

  if (!isCallCode && min(trimValue, PHONE_CALL_CODE_MIN_LENGTH) && phoneFieldType) {
    return PHONE_CALL_CODE_INCORRECT_TEXT
  }

  if (!isCallCode && min(trimValue, PHONE_SMS_CODE_MIN_LENGTH) && phoneFieldType) {
    return ''
  }

  if (!isCallCode && min(trimValue, PHONE_SMS_CODE_MIN_LENGTH) && !phoneFieldType) {
    return PHONE_SMS_CODE_REQUIRED_TEXT
  }

  if (!isCallCode && min(trimValue, PHONE_CALL_CODE_MIN_LENGTH)) {
    return PHONE_CALL_NUMBER_REQUIRED_TEXT
  }

  if (!isCallCode && min(trimValue, PHONE_SMS_CODE_MIN_LENGTH)) {
    return PHONE_SMS_CODE_REQUIRED_TEXT
  }

  return ''
}

export const isInvalidPasswordFormat = (value: string): boolean => {
  if (value.search(/[0-9]/) === -1) return true

  if (value.search(/[a-z]/) === -1) return true

  if (value.search(/[A-Z]/) === -1) return true

  return false
}

export const passwordValidator = (value: string): string => {
  if (!value) return PASSWORD_REQUIRED_TEXT

  if (regex(value, PASSWORD_REGEX)) return PASSWORD_ALLOWED_SYMBOLS_TEXT

  if (min(value, PASSWORD_MIN_LENGTH)) return PASSWORD_MIN_LENGTH_TEXT

  if (max(value, PASSWORD_MAX_LENGTH)) return PASSWORD_MAX_LENGTH_TEXT

  if (isInvalidPasswordFormat(value)) return PASSWORD_REQUIRED_SYMBOLS_TEXT

  return ''
}

export const organization = async (value: IFormValue): Promise<string> => {
  const emailValue = value as IOrganizationsItem
  const trimValue = emailValue.name.trim()

  if (!trimValue) return ORGANIZATION_REQUIRED_TEXT

  if (min(trimValue, ORGANIZATION_MIN_LENGTH)) return ORGANIZATION_MIN_LENGTH_TEXT

  return ''
}

export const staff = async (value: IFormValue): Promise<string> => {
  const stringValue = value as string
  const trimValue = stringValue.trim()

  if (!trimValue) return STAFF_REQUIRED_TEXT

  return ''
}

export const taxation = async (value: IFormValue): Promise<string> => {
  const stringValue = value as string
  const trimValue = stringValue.trim()

  if (!trimValue) return TAXATION_REQUIRED_TEXT

  return ''
}

export const birthDate = async (value: IFormValue): Promise<string> => {
  const stringValue = value as string
  const date: Date = parse(stringValue, DATE_MASK, new Date())
  const age = calculateAge(date)
  const year = date.getUTCFullYear()
  const isDateInvalid = !isValid(date)

  if (stringValue === getPreparedMask(MASK_CHAR) || !stringValue) {
    return BIRTH_DATE_REQUIRED_TEXT
  }

  if (isDateInvalid || age < MINIMAL_AGE || year < MINIMAL_YEAR) {
    return BIRTH_DATE_INCORRECT_TEXT
  }

  return ''
}

export const position = async (value: IFormValue): Promise<string> => {
  const positionValue = value as IOrganizationsItem
  const trimValue = positionValue.name.trim()

  if (!trimValue) return POSITION_REQUIRED_TEXT
  if (min(trimValue, POSITION_MIN_LENGTH)) return POSITION_MIN_TEXT

  return ''
}

export const getPhoneRequiredText = (locale: Languages, language: Languages) =>
  locale === Languages.UZ && language === Languages.RU ? PHONE_REQUIRED_TEXT_UZ : PHONE_REQUIRED_TEXT

const getPhoneInvalidText = (locale: Languages, language: Languages) =>
  locale === Languages.UZ && language === Languages.RU ? PHONE_INVALID_TEXT_UZ : PHONE_INVALID_TEXT

export const phone = (locale: Languages, language: Languages, deployEnv: DeployTypes) => {
  return async (value: IFormValue): Promise<string> => {
    const stringValue = value as string
    const trimValue = stringValue.replace('+', '')

    if (!trimValue) return getPhoneRequiredText(locale, language)

    const appropriateOption = getValidationOptions(stringValue, deployEnv)
    const formattedValue = formatPhoneValue(stringValue, appropriateOption)
    const isPhoneValid = checkPhoneValid(formattedValue, deployEnv, locale)

    if (!isPhoneValid) {
      return getPhoneInvalidText(locale, language)
    }

    return ''
  }
}

export const mockTrue = () => true

const compares: Compares = {
  email: stringCompare,
  firstName: stringCompare,
  lastName: stringCompare,
  middleName: stringCompare,
  organization: dictionaryItemCompare,
  password: stringCompare,
  phone: stringCompare,
  position: dictionaryItemCompare,
  postStudent: dictionaryItemCompare,
  postStudentOrTeacher: dictionaryItemCompare,
  regionId: dictionaryItemCompare,
  instituteId: dictionaryItemCompare,
  degreeId: dictionaryItemCompare,
  specialtyId: dictionaryItemCompare,
  university: dictionaryItemCompare,
  activationCodes: mockTrue,
  smsCode: mockTrue,
  mode: mockTrue,
  branch: mockTrue,
  code: mockTrue,
  staff: stringCompare,
  taxation: stringCompare,
  birthDate: mockTrue,
}

export const useFieldValidators = () => {
  const deployEnv = useEnv()
  const checkEmailRegistration = useCheckEmailRegistration()
  const { locale } = useContext(LocaleContext)
  const { language } = useContext(LanguageContext)
  const allowedNameSymbolsRegex = getLanguageRegExp(language, locale)

  const emailBusy = async (value: string): Promise<{ isBusy: boolean; isTemp: boolean }> => {
    const email = value.trim()

    try {
      const data = await checkEmailRegistration({ email })
      const { isValid, isTemp, isBusy } = data

      if (isTemp) {
        return { isBusy: false, isTemp: true }
      }

      if (isValid && isBusy) {
        return { isBusy: true, isTemp: false }
      }
    } catch (error) {}

    return { isBusy: false, isTemp: false }
  }

  const email = async (value: IFormValue, callbackBusy?: () => void): Promise<string> => {
    const emailValue = value as string
    const trimValue = emailValue.trim()

    if (!trimValue) return EMAIL_REQUIRED_TEXT

    if (max(trimValue, EMAIL_MAX_LENGTH)) return EMAIL_MAX_LENGTH_TEXT

    if (regex(trimValue, MAIL_REGEX)) return EMAIL_FORMAT_TEXT

    const { isBusy, isTemp }: { isBusy: boolean; isTemp: boolean } = await emailBusy(trimValue)

    if (isBusy) {
      if (callbackBusy) {
        callbackBusy()
      }

      return EMAIL_ALREADY_EXISTS_TEXT
    }

    if (isTemp) {
      return EMAIL_TEMP_TEXT
    }

    return ''
  }

  const firstName = async (value: IFormValue): Promise<string> => {
    const stringValue = value as string
    const trimValue = stringValue.trim()

    if (!trimValue) return FIRST_NAME_REQUIRED_TEXT
    if (min(trimValue, NAME_MIN_LENGTH)) return FIRST_NAME_MIN_LENGTH_TEXT
    if (max(trimValue, NAME_MAX_LENGTH)) return FIRST_NAME_MAX_LENGTH_TEXT
    if (regex(trimValue, allowedNameSymbolsRegex))
      return locale === Languages.UZ && language === Languages.RU
        ? FIRST_NAME_ALLOWED_UZ_SYMBOLS_TEXT
        : FIRST_NAME_ALLOWED_SYMBOLS_TEXT

    return ''
  }

  const middleName = async (value: IFormValue): Promise<string> => {
    const stringValue = value as string
    const trimValue = stringValue.trim()

    if (!trimValue) return MIDDLE_NAME_REQUIRED_TEXT

    if (min(trimValue, NAME_MIN_LENGTH)) return MIDDLE_NAME_MIN_LENGTH_TEXT

    if (max(trimValue, NAME_MAX_LENGTH)) return MIDDLE_NAME_MAX_LENGTH_TEXT

    if (regex(trimValue, allowedNameSymbolsRegex))
      return locale === Languages.UZ && language === Languages.RU
        ? MIDDLE_NAME_ALLOWED_UZ_SYMBOLS_TEXT
        : MIDDLE_NAME_ALLOWED_SYMBOLS_TEXT

    return ''
  }

  const lastName = async (value: IFormValue): Promise<string> => {
    const stringValue = value as string
    const trimValue = stringValue.trim()

    if (!trimValue) return LAST_NAME_REQUIRED_TEXT

    if (min(trimValue, NAME_MIN_LENGTH)) return LAST_NAME_MIN_LENGTH_TEXT

    if (max(trimValue, NAME_MAX_LENGTH)) return LAST_NAME_MAX_LENGTH_TEXT

    if (regex(trimValue, allowedNameSymbolsRegex))
      return locale === Languages.UZ && language === Languages.RU
        ? LAST_NAME_ALLOWED_UZ_SYMBOLS_TEXT
        : LAST_NAME_ALLOWED_SYMBOLS_TEXT

    return ''
  }

  const validators: Validators = {
    email,
    firstName,
    lastName,
    middleName,
    organization,
    password,
    phone: phone(locale, language, deployEnv),
    position,
    postStudent,
    postStudentOrTeacher,
    regionId,
    instituteId,
    specialtyId,
    degreeId,
    university,
    staff,
    taxation,
    activationCodes: () => Promise.resolve(''),
    code: () => Promise.resolve(''),
    smsCode: () => Promise.resolve(''),
    mode: () => Promise.resolve(''),
    branch: () => Promise.resolve(''),
    birthDate,
  }

  const addValidators = <InitialFormData extends IFormValues>(initialFormData: InitialFormData): FieldInit => {
    const fieldInit: FieldInit = {}

    entries(initialFormData).forEach(([key, value]) => {
      fieldInit[key] = {
        value,
        validator: validators[key as FormFieldTypes],
        compare: compares[key as FormFieldTypes],
      }
    })

    return fieldInit
  }

  return {
    addValidators,
  }
}
